import { useAPIQuery } from "@dev2_techwalnut/tw-api-query-hook";
import { Table } from "@dev2_techwalnut/tw-react-crud-dashlite";
import React, { useState } from "react";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import Swal from "sweetalert2";

const columns = [
    {
        accessor: "email",
        header: "Email Address",
        cell: (row) => <>{row?.user?.email}</>,
    },
    {
        accessor: "client_location",
        header: "Client Location",
    },
    {
        accessor: "company_name",
        header: "Company Name",
    },
    {
        accessor: "gst_number",
        header: "GST No",
        cell: (row) => <>{row?.gst_number === "" ? "-" : row?.gst_number}</>,
    },
    {
        accessor: "address",
        header: "Address",
    },
    {
        accessor: "gst_certificate",
        header: "GST Certificate",
        cell: (row) => (
            <a target="_blank" href={row?.gst_certificate}>
                GST Certificate
            </a>
        ),
    },
];

const Client = () => {
    const privileges = secureLocalStorage.getItem("privileges");
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const initialEntries = Number(searchParams.get("entries")) || 10;
    const initialPage = Number(searchParams.get("page")) || 1;
    const initialQueries = searchParams.get("q")?.split(",") || [];
    const initialColumns = searchParams.get("c")?.split(",") || [];

    const [entries, setEntries] = useState(initialEntries);
    const [pageIndex, setPageIndex] = useState(initialPage - 1);
    const [globalFilterValue, setGlobalFilterValue] = useState("");
    const [columnFilterKey, setColumnFilterKey] = useState("");
    const [columnFilterValue, setColumnFilterValue] = useState("");

    const { useFetch } = useAPIQuery({
        key: "client",
        endpoint: "/api/client/",
    });

    const { data, isLoading, isFetching } = useFetch({
        params: {
            page_no: pageIndex + 1,
            entries: entries,
            q: globalFilterValue !== "" ? globalFilterValue : columnFilterValue,
            c: globalFilterValue === "" ? columnFilterKey : "",
        },
        abortOnNewRequest: true,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 3,
        retry: 3,
        keepPlaceholder: true,
        refetchOnReconnect: true,
    });

    const { useDelete } = useAPIQuery({
        key: "client",
        endpoint: "/api/client/",
    });

    const { mutate: deleteData } = useDelete();

    const handleGlobalFilterChange = (value) => {
        const q = value?.trim() || "";
        if (q !== "") {
            setGlobalFilterValue(q);
            setColumnFilterKey("");
            setColumnFilterValue("");
            setSearchParams((searchParams) => {
                searchParams.set("q", q);
                searchParams.delete("c");
                return searchParams;
            });
        } else if (q === "") {
            setGlobalFilterValue("");
            setSearchParams((searchParams) => {
                searchParams.delete("q");
                return searchParams;
            });
        }
    };

    const handleColumnFilterChange = (value) => {
        const q = value.map((val) => val.value?.trim() || "").join(",");
        const c = value.map((val) => val.id).join(",");
        if (q !== "" && c !== "") {
            setGlobalFilterValue("");
            setColumnFilterKey(c);
            setColumnFilterValue(q);
            setSearchParams((searchParams) => {
                searchParams.set("q", q);
                searchParams.set("c", c);
                return searchParams;
            });
        } else if (q === "" && c === "") {
            setColumnFilterKey("");
            setColumnFilterValue("");
            setSearchParams((searchParams) => {
                searchParams.delete("q");
                searchParams.delete("c");
                return searchParams;
            });
        }
    };

    const handlePagination = (value) => {
        setSearchParams((searchParams) => {
            searchParams.set("entries", value.pageSize);
            searchParams.set("page", value.pageIndex + 1);
            return searchParams;
        });
        setPageIndex(value.pageIndex);
        setEntries(value.pageSize);
    };

    const handleEditRow = (row) => {
        console.log(row);
        navigate("/edit-client", { state: { editpost: row.original } });
    };

    const handleDeleteRow = (row) => {
        console.log(row);
        const entry_id = row.original.id;
        Swal.fire({
            text: "Are you sure? You will not be able to recover this data!",
            showDenyButton: true,
            confirmButtonText: "Yes",
            denyButtonText: `No, cancel!`,
        }).then((result) => {
            if (result.isConfirmed) {
                deleteData({ id: entry_id });
            }
        });
    };

    return (
        <>
            <div className="nk-block-head nk-block-head-sm">
                <div className="nk-block-between">
                    <div className="nk-block-head-content d-flex">
                        <h3 className="nk-block-title page-title">Client</h3>
                    </div>
                    {privileges.includes("client_create") && (
                        <div className="nk-block-head-content">
                            <NavLink
                                to="/create-client"
                                className="toggle btn btn-icon btn-primary d-md-none"
                                id="button-button-create-client"
                            >
                                <em className="icon ni ni-plus"></em>
                            </NavLink>

                            <NavLink
                                to="/create-client"
                                className="toggle btn btn-primary d-none d-md-inline-flex"
                                id="button-button-create-client"
                            >
                                <em className="icon ni ni-plus"></em>
                                <span>Create Client</span>
                            </NavLink>
                        </div>
                    )}
                </div>
            </div>

            <Table
                data={data ? data?.results.data : []}
                columns={columns}
                pagination
                pageIndex={pageIndex}
                pageSize={entries}
                rowCount={data ? data.count : 0}
                globalSearch
                isLoading={isLoading || isFetching}
                editRows={privileges.includes("client_update") ? true : false}
                deleteRows={privileges.includes("client_delete") ? true : false}
                filterModal={false}
                selectRows={false}
                defaultGlobalSearchValue={
                    initialQueries.length > 0 && initialColumns.length <= 0
                        ? initialQueries[0]
                        : undefined
                }
                defaultColumnFiltersValue={
                    initialQueries.length > 0 && initialColumns.length > 0
                        ? initialColumns.map((column, index) => ({
                              id: column,
                              value: initialQueries?.[index] ?? "",
                          }))
                        : undefined
                }
                onGlobalFilterChange={handleGlobalFilterChange}
                onColumnFilterChange={handleColumnFilterChange}
                onRowEdit={handleEditRow}
                onRowDelete={handleDeleteRow}
                onPaginationChange={handlePagination}
            />
        </>
    );
};

export default Client;
